import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/auth";
import { activityTracker, ActivityType } from "@/services/activityTrackingService";

export function useActivityTracking(activityType: ActivityType = "app_usage") {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const previousUserIdRef = useRef<string | null>(null);
  const activityTypeRef = useRef<ActivityType>(activityType);

  // Keep ref in sync with prop
  activityTypeRef.current = activityType;

  // Simply register activity when a chat message is sent. The session duration
  // is now computed on-demand inside useMessageHandling, so we no longer track
  // timers here.
  const handleMessageSent = useCallback(() => {
    activityTracker.registerActivity();
  }, []);

  // Effect to handle user authentication changes and tracking start/stop
  useEffect(() => {
    const currentUserId = user?.id || null;

    // Only proceed if user ID actually changed
    if (currentUserId === previousUserIdRef.current) {
      return;
    }

    previousUserIdRef.current = currentUserId;

    // User is logged in - start tracking if not already tracking
    if (user) {
      activityTracker.setUserId(user.id);

      if (!isTracking) {
        activityTracker.startTracking(activityType);
        setIsTracking(true);
      }
    }
    // User logged out - stop tracking if currently tracking
    else if (isTracking) {
      activityTracker.stopTracking();
      setIsTracking(false);
      // nothing special to clean up now that we removed the session timer
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activityType]); // Removed isTracking and user dependencies to prevent loops

  // Effect to change activity type when this hook mounts with a specific type
  // and reset to app_usage when it unmounts
  useEffect(() => {
    // Only change activity type if user is authenticated and we're tracking
    if (!user || !isTracking) return;

    // Change to the requested activity type when mounting
    if (activityType !== "app_usage") {
      activityTracker.changeActivityType(activityType);
    }

    // Cleanup: reset to app_usage when this hook unmounts
    // This ensures page-specific activity types (like tutor_chat) don't persist
    // after the user navigates away from that page
    return () => {
      if (activityTypeRef.current !== "app_usage") {
        // Flush current activity before changing type
        activityTracker.flushActivity();
        // Reset to default app_usage type
        activityTracker.changeActivityType("app_usage");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isTracking, activityType]); // user object reference changes but user.id is stable

  // Set up activity listeners only when we have a user and are tracking
  useEffect(() => {
    if (!user || !isTracking) return;

    const activityEvents = ["mousedown", "keydown", "mousemove", "scroll", "touchstart"];
    const handleUserActivity = () => activityTracker.registerActivity();

    // Register all activity event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // Visibility change detection - pause tracking when tab is hidden
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      activityTracker.setTabVisible(isVisible);

      if (document.hidden) {
        // Tab hidden: flush current activity and pause tracking
        // This ensures time spent in other tabs is NOT counted
        activityTracker.flushActivity();
      } else {
        // Tab visible again: reset the start time to now
        // This prevents counting the time user was away
        activityTracker.registerActivity();
      }
    };

    // Set initial visibility state
    activityTracker.setTabVisible(!document.hidden);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Before unload handler to log final activity
    const handleBeforeUnload = () => {
      activityTracker.flushActivity();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function
    return () => {
      if (user) {
        activityTracker.flushActivity();
      }

      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isTracking]); // user object reference changes but user.id is stable

  return {
    changeActivityType: activityTracker.changeActivityType.bind(activityTracker),
    isTracking,
    handleMessageSent,
    getCurrentSeconds: () => activityTracker.getCurrentSeconds(),
    setPage: (page: Parameters<typeof activityTracker.setPage>[0]) => activityTracker.setPage(page),
    setComponent: (component: Parameters<typeof activityTracker.setComponent>[0]) =>
      activityTracker.setComponent(component),
  };
}
